import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ProfileModalComponent } from './modals/profile-modal.component';
import { Notifications } from './notifications/notifications';
import { AioAppTopBarComponent, AioButtonComponent } from '@aio/ui';
import { BrandingService } from '../../../core/services/branding.service';
import { AdminPortalReturnUrlService } from '../../../core/services/admin-portal-return-url.service';
import { ThemeService } from '../../../core/services/theme.service';
@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ProfileModalComponent,
    Notifications,
    AioAppTopBarComponent,
    AioButtonComponent,
  ],
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})
export class TopbarComponent implements OnInit, OnDestroy {
  protected readonly branding = inject(BrandingService);
  private readonly adminPortalReturnUrlService = inject(AdminPortalReturnUrlService);
  protected readonly themeService = inject(ThemeService);


  @Input() pageName: string = '';
  @Input() userInitials: string = '';
  @Input() userName: string = '';
  @Input() userEmail: string = '';
  @Input() userRole: string = '';
  @Input() userProfilePicture: string = '';
  @Input() sidebarCollapsed: boolean = false;
  @Input() showNotifications: boolean = false;

  @Output() signOut = new EventEmitter<void>();
  @Output() profileSettings = new EventEmitter<void>();
  @Output() adminPortal = new EventEmitter<void>();
  @Output() toggleSidebar = new EventEmitter<void>();

  profileModalOpen = signal(false);
  iconPosition = signal({ top: 0, right: 0 });
  isAdminMode = signal(false);
  showCrewAppLink = signal(false);

  constructor(private router: Router) {}

  ngOnInit() {
    this.syncRouteChrome(this.router.url);

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.syncRouteChrome(event.urlAfterRedirects);
    });
  }

  ngOnDestroy() {
    // Cleanup if needed
  }

  private syncRouteChrome(url: string): void {
    const isAdmin = url.includes('/admin');
    this.isAdminMode.set(isAdmin);
    this.showCrewAppLink.set(/\/finance\/job-costing(\/|$|\?)/.test(url));
  }

  toggleDarkMode() {
    this.themeService.toggle();
  }

  openProfileModal(event: MouseEvent) {
    const rect = (event.target as HTMLElement).getBoundingClientRect();

    this.iconPosition.set({
      top: rect.bottom + 10, // Position below the profile picture
      right: window.innerWidth - rect.right - 10 // Position to the right of profile picture
    });
    this.profileModalOpen.set(true);
  }
  closeProfileModal() {
    this.profileModalOpen.set(false);
  }
  goToProfileSettings() {
    this.profileSettings.emit();
    this.closeProfileModal();
  }
  goToAdminPortal() {
    this.adminPortal.emit();
    this.closeProfileModal();
  }

  exitAdminPortal(): void {
    const currentUrl = this.router.url;
    const tenantMatch = currentUrl.match(/\/([^\/]+)\//);
    const tenantId = tenantMatch ? tenantMatch[1] : null;

    if (tenantId) {
      const returnUrl = this.adminPortalReturnUrlService.getReturnUrlForTenant(tenantId);
      if (returnUrl) {
        this.router.navigateByUrl(returnUrl);
      } else {
        this.router.navigate([`/${tenantId}/leads`]);
      }
    } else {
      this.router.navigate(['/login']);
    }
    this.closeProfileModal();
  }

  logout() {
    this.signOut.emit();
    this.closeProfileModal();
  }

  /** First URL segment is tenant id for all authenticated shell routes (e.g. `/acme-corp/leads`). */
  homeRouterLink(): (string | number)[] {
    const tenantId = this.router.url.split(/[/?#]/).filter(Boolean)[0];
    return tenantId ? ['/', tenantId, 'home'] : ['/login'];
  }
}
